import React from 'react';
import { useTranslation } from 'react-i18next';

import { SearchResultsHeadless, SearchResultsList } from '@uncinc/react-searchkit';

import type {
  SearchAllContentAnnualReportHit,
  SearchAllContentArticleHit,
  SearchAllContentDepartmentHit,
  SearchAllContentEquipmentHit,
  SearchAllContentExpertiseHit,
  SearchAllContentHit,
  SearchAllContentPartnerHit,
  SearchAllContentDefaultProfileHit,
  SearchAllContentProjectHit,
  SearchAllContentThemeHit,
  SearchAllContentVacancyHit,
  SearchAllContentIMISPublicationHit,
  SearchAllContentORCIDPublicationHit,
} from '../../../SearchAllContent/types';

import SearchAllContentPagination from '../../../SearchAllContent/Pagination';
import SearchAllContentSearchBar from '../../../SearchAllContent/SearchBar';
import MetaSearchEmptyState from '../EmptyState';
import MetaSearchFacets from '../Facets';
import { getSearchbarPlaceholderKey, sortByCustomOrder } from '../metaParagraphsHelpers';
import type { MetaSearchProps } from '..';

import ArticleHit from '../Results/ArticleHit';
import ProjectHit from '../Results/ProjectHit';
import ProfileHit from '../Results/ProfileHit';
import DepartmentHit from '../Results/DepartmentHit';
import ThemeHit from '../Results/ThemeHit';
import EquipmentHit from '../Results/EquipmentHit';
import ExpertiseHit from '../Results/ExpertiseHit';
import VacancyHit from '../Results/VacancyHit';
import PartnerHit from '../Results/PartnerHit';
import AnnualReportHit from '../Results/AnnualReportHit';
import MetaSearchIMISPublicationHit from '../Results/IMISPublicationHit';
import MetaSearchORCIDPublicationHit from '../Results/ORCIDPublicationHit';

const RenderItem = (hit: SearchAllContentHit | SearchAllContentIMISPublicationHit | SearchAllContentORCIDPublicationHit) => {
  let type;

  if ('content_type' in hit) {
    const { content_type: contentType } = hit;
    [type] = contentType;
  } else if ('vid' in hit) {
    const { vid } = hit;
    [type] = vid;
  } else if ('profile_type' in hit) {
    const { profile_type: profileType } = hit;
    [type] = profileType;
  } else if ('imis_publication_title' in hit) {
    type = 'imis_publication_nioz';
  } else if ('orcid_publication_title' in hit) {
    type = 'orcid_publication_nioz';
  }

  switch (type) {
    case 'article': {
      return <ArticleHit hit={hit as SearchAllContentArticleHit} />;
    }
    case 'project': {
      return <ProjectHit hit={hit as SearchAllContentProjectHit} />;
    }
    case 'default_profile': {
      return <ProfileHit hit={hit as SearchAllContentDefaultProfileHit} />;
    }
    case 'departments': {
      return <DepartmentHit hit={hit as SearchAllContentDepartmentHit} />;
    }
    case 'themes': {
      return <ThemeHit hit={hit as SearchAllContentThemeHit} />;
    }
    case 'equipment': {
      return <EquipmentHit hit={hit as SearchAllContentEquipmentHit} />;
    }
    case 'expertises': {
      return <ExpertiseHit hit={hit as SearchAllContentExpertiseHit} />;
    }
    case 'vacancy': {
      return <VacancyHit hit={hit as SearchAllContentVacancyHit} />;
    }
    case 'partner': {
      return <PartnerHit hit={hit as SearchAllContentPartnerHit} />;
    }
    case 'annual_report': {
      return <AnnualReportHit hit={hit as SearchAllContentAnnualReportHit} />;
    }
    case 'imis_publication_nioz': {
      return <MetaSearchIMISPublicationHit hit={hit as SearchAllContentIMISPublicationHit} />;
    }
    case 'orcid_publication_nioz': {
      return <MetaSearchORCIDPublicationHit hit={hit as SearchAllContentORCIDPublicationHit} />;
    }
    default: {
      return <div />;
    }
  }
};

export type MetaSearchInnerProps =
Pick<MetaSearchProps, 'sortingList' | 'withSearchbar' | 'withFacets' | 'withPagination' | 'disableScrollTo' | 'contentType'>;

const MetaSearchInner: React.FC<MetaSearchInnerProps> = (props) => {
  const {
    contentType,
    sortingList = undefined,
    withSearchbar = false,
    withFacets = false,
    withPagination = false,
    disableScrollTo = false,
  } = props;

  const { t } = useTranslation();

  return (
    <div className="MetaSearch">
      {withSearchbar && (
        <SearchAllContentSearchBar
          className="MetaSearch__SearchBar"
          title={t('searchkit.search_bar.title')}
          placeholder={t(getSearchbarPlaceholderKey(contentType))}
        />
      )}

      {withFacets && (
        <MetaSearchFacets />
      )}

      <div className="ParagraphMeta__results">
        <SearchResultsHeadless>
          {(hits) => {
            const sortedItems = sortingList ? sortByCustomOrder(hits, sortingList) : hits;

            return (
              <SearchResultsList
                renderItem={RenderItem}
                hits={sortedItems}
                emptyState={<MetaSearchEmptyState />}
              />
            );
          }}
        </SearchResultsHeadless>
      </div>

      {withPagination && (
        <SearchAllContentPagination disableScrollTo={disableScrollTo} className="MetaSearch__Pagination" />
      )}
    </div>
  );
};

export default MetaSearchInner;
